import * as React from "react";
import { X, AlertTriangle } from "lucide-react";
import { KnowledgeBaseDocument } from "../../../../types/navigationTypes";
import { Button } from "@/components/ui/button";
import FileIconSVG from "../../../../shared/components/FileIconSVG";

interface DocumentViewerProps {
  document: KnowledgeBaseDocument;
  onClose: () => void;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ document, onClose }) => {
  const getFileExtension = (fileName: string, url?: string): string => {
    // 1. Intentar desde el nombre
    if (fileName && fileName.includes('.')) {
      const parts = fileName.split('.');
      const ext = parts[parts.length - 1].toLowerCase();
      if (ext) return ext;
    }
    
    // 2. Intentar desde la URL
    if (url) {
      try {
        const urlPath = url.split('?')[0];
        const parts = urlPath.split('.');
        const ext = parts[parts.length - 1].toLowerCase();
        if (ext && ext.length <= 5) return ext;
      } catch (e) {
        // Ignorar
      }
    }
    
    return '';
  };

  const getViewerUrl = (url: string, fileName: string): string => {
    const extension = getFileExtension(fileName, url);
    
    // For PDFs, use the URL directly in iframe
    if (extension === 'pdf') {
      return url;
    }
    
    // For Office documents, use Office Online Viewer in EMBED mode (simpler UI)
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension)) {
      // Usar embed.aspx en lugar de view.aspx para una interfaz más limpia
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    }
    
    // For other formats, try to display directly
    return url;
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Viewer Header */}
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <FileIconSVG 
            filename={document.name} 
            extension={getFileExtension(document.name, document.url || undefined)}
            size={18} 
            className="flex-shrink-0" 
          />
          <h3 className="m-0 text-sm font-medium text-slate-800 overflow-hidden text-ellipsis whitespace-nowrap flex-1">
            {document.name}
          </h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          title="Close viewer"
          className="h-8 w-8 p-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Document Viewer */}
      <div className="flex-1 relative overflow-hidden">
        {document.url ? (
          <iframe
            src={getViewerUrl(document.url, document.name)}
            className="w-full h-full border-0"
            title={document.name}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <AlertTriangle className="h-12 w-12 mb-4" />
            <p>Document URL not available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentViewer;






